import { html, css, nothing, LitElement } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import sharedStyles from '../../app/styles.js';
import { saveVimeoItem, updateVimeoItem } from '../../store/vimeo/vimeo.js';
import {
    extractVimeoData,
    fetchVimeoMetadata,
} from '../../store/vimeo/helpers.js'
import { navigateBack } from '../../store/route/route.js';
import { previewElearning } from '../../store/preview/preview.js';
import { translate } from '../../helpers/translate.js';
import { createSupplierKey } from '../../store/elearning/helpers.js';
import {
    toSelectItems,
    getSelectedItemValue,
    toSelectedItemValue,
} from '../../helpers/controls.js';
import '@sdb-develop/learning-frontend';
import '@vaadin/select/theme/material/vaadin-select.js';
import '@polymer/paper-input/paper-input.js';
import '@polymer/iron-form/iron-form.js';
import '../shared/sdb-loading.js';
import '../shared/sdb-overlay-layout.js';

@customElement('sdb-vimeo-item')
class SdbVimeoItem extends connect(store)(LitElement) {

    @property({ type: String })
    accessor targetId;

    @state()
    accessor vimeoItem;

    @state()
    accessor lastVersion;

    @state()
    accessor isLoading = false;

    @state()
    accessor isDirty = false;

    @state()
    accessor urlInputDirty = false;

    @query('#vimeo_item_form')
    accessor vimeoItemForm;

    @query('#vimeo_uploader')
    accessor vimeoUploader;

    @query('#url_input')
    accessor urlInput;

    get latestVersion() {
        if (!this.vimeoItem.versions?.length)
            return null;
        return this.vimeoItem.versions[this.vimeoItem.versions.length-1];
    }

    get languages() {
        return ['nl', 'en'].map((l) => ({
            value: l,
            label: translate(`language_${l}`),
        }));
    }

    stateChanged(state) {
        this.vimeoItem = state.vimeo?.current;
        this.lastVersion = state.vimeo?.current?.lastVersion;
        this.isLoading = state.vimeo?.status === 'loading' || !state.vimeo?.current;
        this.isDirty = state.vimeo?.dirty;
        this.settings = state.env.settings;
        this.user = state.auth.user;
        this.portalId = state.env.portalIdentifier;
    }

    static get styles() {
        return [
            sharedStyles,
            css`
                [slot=content] {
                    width: 100%;
                    padding: 0 20px;
                    max-width: 1400px;
                }

                sdb-content-card {
                    max-width: 1400px;
                    margin-bottom: 16px;
                }

                sdb-content-card .headline {
                    display: inline-flex;
                    align-items: center;
                    color: var(--text-primary);
                    padding: 10px 0 20px 0;
                }

                sdb-content-card .headline span {
                    margin-left: 24px;
                    font-size: 16px;
                    font-family: var(--font-family);
                }

                paper-input {
                    margin-bottom: 5px;
                }

                paper-input[required] label {
                    color: red;
                }

                paper-toggle-button {
                    margin-top: 15px;
                    margin-bottom: 15px;
                }

                vaadin-select {
                    width: 30%;
                    min-width: 200px;
                }

                .uploader_container {
                    margin-top: 30px;
                }

                .uploader_container > span {
                    display: block;
                    font-size: 14px;
                    margin-bottom: 10px;
                    margin-left: 25px;
                }

                .medium {
                    width: 100%;
                    max-width: 700px;
                }

                .small {
                    width: 100%;
                    max-width: 300px;
                }

                .helper {
                    display: block;
                    margin-top: -10px;
                    margin-bottom: 5px;
                    font-size: 12px;
                    color: rgba(0, 0, 0, 0.6);
                }

                .clear-btn:hover {
                    transform: var(--sdb-button-scale-factor, scale(1.05));
                    cursor: pointer;
                }
            `,
        ];
    }

    render() {
        if (this.isLoading || this.targetId !== this.vimeoItem.targetId) {
            return html`<sdb-loading></sdb-loading>`;
        }

        const update = (changes) => store.dispatch(updateVimeoItem({ changes }));
        const updateLastVersion = (changes) => store.dispatch(updateVimeoItem({
            changes: {
                lastVersion: changes
            }
        }));

        return html`
            <sdb-overlay-layout
                headline=${this.vimeoItem.title}
                ?dirty=${this.isDirty}
                @back=${() => { store.dispatch(navigateBack('vimeo')); }}>

                <div slot="buttons">
                    <sdb-content-button
                        id="preview_button"
                        label=${translate('preview')}
                        primary
                        ?disabled=${this.isDirty}
                        @click=${()=> { store.dispatch(previewElearning({ item: this.vimeoItem })); }}>
                    </sdb-content-button>
                    <sdb-content-button
                        id="save_button"
                        label=${translate('save')}
                        primary
                        ?disabled=${!this.isDirty}
                        @click=${() => {
                            if (this.vimeoItemForm.validate())
                                store.dispatch(saveVimeoItem({ id: this.vimeoItem.id }));
                        }}>
                    </sdb-content-button>
                </div>

                <iron-form id="vimeo_item_form" slot="content">
                    <form>
                        <sdb-layout-100>

                            <sdb-content-card id="general_card">
                                <paper-input
                                    id="name_input"
                                    transparent
                                    label=${translate('name')}
                                    value=${this.vimeoItem.title}
                                    @change=${(e) => update({ title: e.target.value })}>
                                </paper-input>

                                <paper-input
                                    id="description_textarea"
                                    transparent
                                    label=${translate('description')}
                                    value=${this.vimeoItem.description}
                                    @change=${(e) => update({ description: e.target.value })}>
                                </paper-input>

                                <paper-input
                                    id="supplier_input"
                                    transparent
                                    label=${translate('supplier')}
                                    value=${this.vimeoItem.supplier.name}
                                    @change=${(e) => update({
                                        supplier: {
                                            name: e.target.value,
                                            supplierKey: createSupplierKey(e.target.value),
                                        }
                                    })}>
                                </paper-input>

                            </sdb-content-card>

                            <sdb-content-card>
                                <sdb-layout-80-20>
                                    <div slot="left">
                                        <paper-input
                                            id="url_input"
                                            label=${translate('vimeo_url')}
                                            value=${this.lastVersion.startData.url}
                                            @value-changed=${(e) => this.urlInputDirty = this.lastVersion.startData.url !== this.urlInput.value}>
                                            <sdb-content-icon
                                                icon="fal fa-xmark"
                                                slot="suffix"
                                                class="clear-btn"
                                                @click=${() => {
                                                    this.urlInput.value = '';
                                                    this.urlInput.inputElement.focus();
                                                }}>
                                            </sdb-content-icon>
                                        </paper-input>
                                        <span class="helper">${translate('vimeoUrlParseHelper')}</span>
                                    </div>
                                    <div slot="right">
                                        <sdb-content-button
                                            secondary
                                            label=${translate('parseUrl')}
                                            ?disabled=${!this.urlInputDirty}
                                            @click=${() => {
                                                this._parseUrl(this.urlInput.value);
                                                this.urlInputDirty = false;
                                            }}>
                                        </sdb-content-button>
                                    </div>
                                </sdb-layout-80-20>
                            </sdb-content-card>

                            <sdb-content-card>
                                <sdb-layout-50-50>
                                    <div slot="left">

                                        <paper-input
                                            readonly
                                            id="videoid_input"
                                            class="small"
                                            label=${translate('vimeo_videoId')}
                                            value=${this.lastVersion.startData.videoId}>
                                        </paper-input>

                                        ${this.lastVersion.startData.hash ? html`
                                            <paper-input
                                                readonly
                                                id="hash_input"
                                                class="small"
                                                label=${translate('vimeo_hash')}
                                                value=${this.lastVersion.startData.hash}>
                                            </paper-input>
                                            <span class="helper">${translate('vimeoHashHelper')}</span>
                                        ` : nothing}

                                        <paper-toggle-button
                                            id="autoplay_toggle"
                                            ?checked=${this.lastVersion.startData.autoplay}
                                            @change=${(e) => updateLastVersion({
                                                startData: {
                                                    autoplay: e.target.checked,
                                                }
                                            })}>
                                            <span>${translate('vimeo_autoplay')}</span>
                                        </paper-toggle-button>

                                        <paper-toggle-button
                                            id="mute_toggle"
                                            ?checked=${this.lastVersion.startData.muted}
                                            @change=${(e) => updateLastVersion({
                                                startData: {
                                                    muted: e.target.checked,
                                                }
                                            })}>
                                            <span>${translate('vimeo_mute')}</span>
                                        </paper-toggle-button>

                                        <paper-input
                                            id="startAt_input"
                                            class="small"
                                            label=${translate('vimeo_startAt')}
                                            placeholder="00:00:00"
                                            type="time"
                                            step="1"
                                            value=${this._formatTime(this.lastVersion.startData.startAt)}
                                            @change=${(e) => updateLastVersion({
                                                startData: {
                                                    startAt: this._parseTime(e.target.value),
                                                }
                                            })}>
                                        </paper-input>

                                        ${this.lastVersion.startData.endAt ? html`
                                            <paper-input
                                                readonly
                                                id="endAt_input"
                                                class="small"
                                                label=${translate('vimeo_endAt')}
                                                type="time"
                                                value=${this._formatTime(this.lastVersion.startData.endAt)}>
                                            </paper-input>
                                            <span class="helper">${translate('vimeoEndAtHelper')}</span>
                                        ` : nothing}

                                        <vaadin-select
                                            id="captions_select"
                                            transparent
                                            class="small"
                                            label=${translate('vimeo_captions')}
                                            value=${toSelectedItemValue(this.lastVersion.startData?.captions)}
                                            @change=${(e) => updateLastVersion({
                                                startData: {
                                                    captions: getSelectedItemValue(e),
                                                }
                                            })}
                                            .items=${toSelectItems(this.languages, translate('no_captions'))}>
                                        </vaadin-select>
                                    </div>
                                    <div slot="right">
                                        <img src=${this.vimeoItem.thumbnail}>
                                    </div>
                                </sdb-layout-50-50>
                            </sdb-content-card>

                        </sdb-layout-100>
                    </form>
                </iron-form>
            </sdb-overlay-layout>
        `;
    }

    _reverse(items) {
        return [...items].reverse();
    }

    _parseTime(input) {
        if (!input) return null;

        const parts = input.split(':').map(Number);

        if (parts.length === 3) {
            return parts[0] * 3600 + parts[1] * 60 + parts[2];
        }

        if (parts.length === 2) {
            return parts[0] * 60 + parts[1];
        }

        return Number(input) || 0;
    }

    _formatTime(seconds) {
        if (seconds == null) return '00:00:00';

        const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
        const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
        const s = String(seconds % 60).padStart(2, '0');

        return `${h}:${m}:${s}`;
    }

    async _parseUrl(url) {
        const {
            videoId,
            hash,
            startAt,
            endAt,
        } = extractVimeoData(url);
        if (!videoId)
            return;

        const {
            title,
            author_name,
            thumbnail_url,
        } = await fetchVimeoMetadata(videoId, hash);

        store.dispatch(updateVimeoItem({
            changes: {
                title: title,
                supplier: {
                    name: author_name,
                    supplierKey: createSupplierKey(author_name),
                },
                image: thumbnail_url,
                thumbnail: thumbnail_url,
                lastVersion: {
                    title: title,
                    startData: {
                        url: url,
                        videoId: videoId,
                        hash: hash,
                        startAt: startAt,
                        endAt: endAt,
                        thumbnailUrl: thumbnail_url,
                    }
                }
            }
        }));
    }
}

